import { getQuorumStatus } from './quorumUtils';
import {
  getElectionNextStep,
  getElectionOfficeLabel,
} from './electionChairScript';

/**
 * Deterministic “next step” for the Chair Dashboard (not AI).
 * @returns {{ title: string, detail: string }}
 */
export function getChairNextStep({
  currentMotion = null,
  pendingUCRequest = null,
  currentAgendaItem = null,
  meetingSetup = {},
  currentSpeaker = null,
  quorumStatus = null,
  inElectionMode = false,
  electionScreen = 1,
  currentElection = null,
  electionNominees = [],
  currentCandidate = null,
} = {}) {
  const quorum = quorumStatus || getQuorumStatus(meetingSetup);

  if (quorum && quorum.present === false) {
    return {
      title: 'Establish quorum',
      detail: 'Business cannot proceed until enough members are present.',
    };
  }

  if (pendingUCRequest) {
    return {
      title: 'Unanimous consent',
      detail: 'Ask if there is any objection, then grant or note the objection.',
    };
  }

  if (inElectionMode) {
    return getElectionNextStep({
      electionScreen,
      officeLabel: getElectionOfficeLabel(currentElection),
      electionNominees,
      currentCandidate,
    });
  }

  if (currentSpeaker) {
    return {
      title: 'Member speaking',
      detail: `${currentSpeaker} has the floor. Keep debate orderly; advance when they finish.`,
    };
  }

  if (currentMotion) {
    switch (currentMotion.status) {
      case 'pending_second':
        return {
          title: 'Await a second',
          detail: 'Ask: “Is there a second?” Then record the second on the Driver console.',
        };
      case 'seconded':
        return {
          title: 'State the question',
          detail: 'State the motion clearly to the assembly, then open debate if it is debatable.',
        };
      case 'stated':
      case 'open_debate':
        return {
          title: 'Preside over debate',
          detail: 'Recognize speakers in order. When debate is finished, put the question.',
        };
      case 'close_debate':
        return {
          title: 'Put the question',
          detail: 'Restate the motion and call for the vote (or remote ballot).',
        };
      case 'ready_for_vote':
        return {
          title: 'Take the vote',
          detail: 'Collect the tally, then announce the result.',
        };
      case 'voted_passed':
      case 'voted_failed':
        return {
          title: 'Announce the result',
          detail: 'Declare whether the motion carried or failed, then move to the next business.',
        };
      case 'completed':
        return {
          title: 'Next business',
          detail: 'This motion is finished. Advance the agenda or recognize the next item.',
        };
      default:
        break;
    }
  }

  const agendaTitle = currentAgendaItem?.title || '';
  if (agendaTitle && /call to order/i.test(agendaTitle)) {
    return {
      title: 'Call to order',
      detail: 'Call the meeting to order and start the meeting timer when ready.',
    };
  }
  if (agendaTitle && /report/i.test(agendaTitle)) {
    return {
      title: 'Receive reports',
      detail: 'Recognize the officer or committee for their report, then complete the agenda item.',
    };
  }
  if (agendaTitle && /new business/i.test(agendaTitle)) {
    return {
      title: 'New business',
      detail: 'Recognize members for main motions. The secretary enters them on the Driver console.',
    };
  }

  if (agendaTitle) {
    return {
      title: 'Current agenda',
      detail: `You are on “${agendaTitle}”. Complete it when finished, then advance.`,
    };
  }

  return {
    title: 'Ready for business',
    detail: 'Recognize members or advance the agenda as appropriate.',
  };
}
